import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { adminKycAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import {
  ArrowLeft, Copy, ExternalLink, CheckCircle, XCircle,
  Clock, AlertCircle, FileText, Download, Eye,
} from 'lucide-react';

const C = {
  bg: '#f4f4f9', surface: '#FFFFFF', border: '#e8e8f1',
  ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  primary: '#7c3aed', success: '#10B981', danger: '#EF4444',
  warning: '#F59E0B', info: '#06B6D4', radius: 14,
};

const STATUS_META = {
  active:    { color: C.info,    bg: '#EFF9FF', icon: Clock,        label: 'Active — Awaiting merchant submission' },
  submitted: { color: C.warning, bg: '#FFFBEB', icon: AlertCircle,  label: 'Submitted — Pending review'           },
  approved:  { color: C.success, bg: '#F0FDF4', icon: CheckCircle,  label: 'Approved'                             },
  rejected:  { color: C.danger,  bg: '#FEF2F2', icon: XCircle,      label: 'Rejected'                             },
};

const Btn = ({ onClick, color = C.primary, children, disabled, size = 'md', style = {} }) => (
  <button onClick={onClick} disabled={disabled} style={{
    background: color, color: '#fff', border: 'none',
    borderRadius: size === 'sm' ? 7 : 9,
    padding: size === 'sm' ? '6px 12px' : '10px 20px',
    fontSize: size === 'sm' ? 12 : 13, fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    display: 'inline-flex', alignItems: 'center', gap: 6, ...style,
  }}>{children}</button>
);

const SectionCard = ({ title, icon: Icon, children, accent = C.primary }) => (
  <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: C.radius, marginBottom: 20, overflow: 'hidden' }}>
    <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
      {Icon && <Icon size={15} color={accent} />}
      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.ink }}>{title}</h4>
    </div>
    <div style={{ padding: '18px 20px' }}>{children}</div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 16, padding: '9px 0', borderBottom: `1px solid ${C.border}`, alignItems: 'flex-start' }}>
    <span style={{ fontSize: 12, color: C.muted, width: 150, flexShrink: 0, paddingTop: 1 }}>{label}</span>
    <span style={{ fontSize: 13, color: C.ink, fontWeight: 500, wordBreak: 'break-all' }}>{value ?? '—'}</span>
  </div>
);

/* File preview tile */
const FileTile = ({ label, url }) => {
  const isPdf = url?.toLowerCase().endsWith('.pdf');
  return (
    <div style={{
      border: `1.5px solid ${C.border}`, borderRadius: 10,
      overflow: 'hidden', background: C.bg,
    }}>
      <div style={{
        height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#F1F5F9', position: 'relative',
      }}>
        {isPdf ? (
          <div style={{ textAlign: 'center' }}>
            <FileText size={36} color={C.muted} />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>PDF Document</div>
          </div>
        ) : (
          <img
            src={url}
            alt={label}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
        )}
        <div style={{ display: 'none', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'absolute', inset: 0 }}>
          <FileText size={36} color={C.muted} />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Preview unavailable</div>
        </div>
      </div>
      <div style={{ padding: '10px 12px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.ink, marginBottom: 8 }}>{label}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <a href={url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
            <Btn size="sm" color={C.primary} style={{ fontSize: 11, padding: '4px 10px' }}>
              <Eye size={11} /> View
            </Btn>
          </a>
          <a href={url} download style={{ textDecoration: 'none' }}>
            <Btn size="sm" color={C.body} style={{ fontSize: 11, padding: '4px 10px' }}>
              <Download size={11} /> Download
            </Btn>
          </a>
        </div>
      </div>
    </div>
  );
};

/* Approve/Reject modal */
const ReviewModal = ({ formId, action, onClose, onDone }) => {
  const [note, setNote]         = useState('');
  const [saving, setSaving]     = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await adminKycAPI.updateStatus(formId, { status: action, note });
      toast.success(`KYC ${action} successfully`);
      onDone();
      onClose();
    } catch {
      toast.error('Failed to update status');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(20,20,43,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 440, padding: 28, boxShadow: '0 24px 64px rgba(0,0,0,.2)' }}>
        <h4 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 700, color: action === 'approved' ? C.success : C.danger }}>
          {action === 'approved' ? '✅ Approve KYC' : '❌ Reject KYC'}
        </h4>
        <p style={{ fontSize: 13, color: C.body, margin: '0 0 14px' }}>
          {action === 'approved'
            ? 'Confirm that all documents have been verified and the merchant is approved.'
            : 'Provide a reason for rejection so the merchant can resubmit.'}
        </p>
        <label style={{ fontSize: 12, fontWeight: 600, color: C.body, display: 'block', marginBottom: 6 }}>
          {action === 'approved' ? 'Note (optional)' : 'Rejection reason'}
        </label>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={3}
          placeholder={action === 'approved' ? 'All documents verified…' : 'Documents unclear, please resubmit…'}
          style={{ width: '100%', padding: '9px 12px', fontSize: 13, border: `1.5px solid ${C.border}`, borderRadius: 8, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
        />
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <Btn onClick={submit} disabled={saving} color={action === 'approved' ? C.success : C.danger}>
            {saving ? 'Saving…' : `Confirm ${action === 'approved' ? 'Approval' : 'Rejection'}`}
          </Btn>
          <Btn onClick={onClose} color={C.muted}>Cancel</Btn>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Detail Page ─── */
const AdminKYCDetail = () => {
  const { id } = useParams();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(null); // 'approved' | 'rejected' | null

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await adminKycAPI.show(id);
      setData(res);
    } catch {
      toast.error('Failed to load KYC details');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>Loading…</div>;
  if (!data)   return <div style={{ padding: 24, color: C.danger }}>KYC form not found.</div>;

  const { form, merchant, submission } = data;
  const statusMeta = STATUS_META[form.status] || STATUS_META.active;
  const StatusIcon = statusMeta.icon;

  const kycUrl = `${window.location.protocol}//${window.location.hostname.replace('localhost','api.a1xpay.com')}/kyc/${form.token}`;

  const fileKeys  = Object.keys(submission?.files  || {});
  const textKeys  = Object.keys(submission?.text_data || {});

  const FIELD_LABELS = {
    aadhaar_front:'Aadhaar Card (Front)', aadhaar_back:'Aadhaar Card (Back)',
    pan_card:'PAN Card', selfie:'Selfie / Live Photo', mobile:'Mobile Number',
    email_id:'Email ID', address:'Current Address',
    gst_certificate:'GST Certificate', udyam_registration:'Udyam Registration',
    cancelled_cheque:'Cancelled Cheque', bank_statement:'Bank Statement',
    account_holder_name:'Account Holder Name', account_number:'Account Number', ifsc_code:'IFSC Code',
    website_url:'Website URL / App', product_details:'Product / Service Details', ad_platform:'Advertisement Platform',
    digital_signature:'Digital Signature', terms_acceptance:'Terms Acceptance',
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>

      {/* ── Back + Header ── */}
      <Link to="/admin/kyc" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.primary, textDecoration: 'none', marginBottom: 20 }}>
        <ArrowLeft size={14} /> Back to KYC Forms
      </Link>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.ink }}>
            KYC Form #{form.id}
          </h2>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, background: statusMeta.bg, color: statusMeta.color, border: `1px solid ${statusMeta.color}33`, borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>
            <StatusIcon size={12} /> {statusMeta.label}
          </div>
        </div>

        {/* Approve / Reject buttons — only when submitted */}
        {form.status === 'submitted' && (
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={() => setModal('approved')} color={C.success}>
              <CheckCircle size={14} /> Approve KYC
            </Btn>
            <Btn onClick={() => setModal('rejected')} color={C.danger}>
              <XCircle size={14} /> Reject KYC
            </Btn>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* ── Left ── */}
        <div>
          {/* Merchant Info */}
          <SectionCard title="Merchant Details" icon={FileText}>
            <InfoRow label="Name"       value={merchant?.username} />
            <InfoRow label="Email"      value={merchant?.email} />
            <InfoRow label="Phone"      value={merchant?.phone} />
            <InfoRow label="MID"        value={<code style={{ fontSize: 12 }}>{merchant?.mid}</code>} />
          </SectionCard>

          {/* Form Info */}
          <SectionCard title="Form Info" icon={FileText}>
            <InfoRow label="Form ID"    value={form.id} />
            <InfoRow label="Created"    value={new Date(form.created_at).toLocaleString('en-IN')} />
            <InfoRow label="Status"     value={form.status.charAt(0).toUpperCase() + form.status.slice(1)} />
            <InfoRow label="Fields"     value={`${(form.fields || {}).length || Object.keys(form.fields || {}).length} fields selected`} />
            {form.admin_note && <InfoRow label="Admin Note" value={form.admin_note} />}
            {submission?.submitted_at && (
              <InfoRow label="Submitted At" value={new Date(submission.submitted_at).toLocaleString('en-IN')} />
            )}
            {submission?.ip_address && (
              <InfoRow label="Submitted IP" value={submission.ip_address} />
            )}
            {submission?.terms_accepted !== undefined && (
              <InfoRow label="Terms Accepted" value={submission.terms_accepted ? '✅ Yes' : '❌ No'} />
            )}
          </SectionCard>

          {/* KYC Link */}
          <SectionCard title="KYC Link" icon={ExternalLink}>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Share this link with the merchant:</div>
            <div style={{
              background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
              padding: '10px 12px', fontFamily: 'monospace', fontSize: 12,
              wordBreak: 'break-all', color: C.body, marginBottom: 12,
            }}>
              {`https://api.a1xpay.com/kyc/${form.token}`}
            </div>
            <Btn size="sm" color={C.primary} onClick={() => {
              navigator.clipboard.writeText(`https://api.a1xpay.com/kyc/${form.token}`);
              toast.success('Link copied!');
            }}>
              <Copy size={12} /> Copy Link
            </Btn>
          </SectionCard>
        </div>

        {/* ── Right ── */}
        <div>
          {/* Text / Info Fields */}
          {textKeys.length > 0 && (
            <SectionCard title="Submitted Information" icon={FileText} accent={C.success}>
              {textKeys.map(key => (
                <InfoRow key={key} label={FIELD_LABELS[key] || key} value={submission.text_data[key]} />
              ))}
            </SectionCard>
          )}

          {/* No submission yet */}
          {!submission && (
            <SectionCard title="Submission" icon={Clock} accent={C.warning}>
              <div style={{ textAlign: 'center', padding: '20px 0', color: C.muted }}>
                <Clock size={32} style={{ marginBottom: 10 }} />
                <p style={{ margin: 0, fontSize: 14 }}>Merchant has not submitted the form yet.</p>
              </div>
            </SectionCard>
          )}
        </div>
      </div>

      {/* ── Documents Grid ── */}
      {fileKeys.length > 0 && (
        <SectionCard title="Uploaded Documents" icon={FileText} accent={C.primary}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
            {fileKeys.map(key => (
              <FileTile
                key={key}
                label={FIELD_LABELS[key] || key}
                url={submission.files[key]}
              />
            ))}
          </div>
        </SectionCard>
      )}

      {/* Review Modal */}
      {modal && (
        <ReviewModal
          formId={form.id}
          action={modal}
          onClose={() => setModal(null)}
          onDone={load}
        />
      )}
    </div>
  );
};

export default AdminKYCDetail;
